import { LucideIcon } from "lucide-react";
import { z } from "zod";

// eslint-disable-next-line local-rules/disallow-prisma-client-import
import {
  Host as PrismaHost,
  Task as PrismaTask,
  App as PrismaApp,
  Journal as PrismaJournal,
  Tenant as PrismaTenant,
  PrivateKey as PrismaPrivateKey,
  File as PrismaFile,
  Service as PrismaService,
  User as PrismaUser,
  Provider as PrismaProvider,
  Provision as PrismaProvision,
  Backup as PrismaBackup,
  Project as PrismaProject,
  Invite as PrismaInvite,
  UserSuggestion as PrismaUserSuggestion
} from "@prisma/client";
import { MessageSchema, SystemMessageType, TransportType } from "@/lib/message";

/** A non-empty array of type T */
export type NonEmptyArray<T> = [T, ...T[]];

/** Make properties K in T required and non-nullable */
export type Require<T, K extends keyof T> = Omit<T, K> &
  Required<Pick<T, K>> & { [P in K]: NonNullable<T[P]> };

export type NonUndefined<T> = T extends undefined ? never : T;

export type OptionalNotUndefined<T, K> = {
  [P in keyof T]: P extends K ? NonUndefined<T[P]> : T[P];
};

export type NavItem = {
  title: string;
  href: string;
  disabled?: boolean;
  icon: LucideIcon | React.ReactNode;
};

export type User = PrismaUser;
export type Invite = PrismaInvite;

export type CompanyType =
  | "developer"
  | "indie-hacker"
  | "self-hosting"
  | "startup"
  | "enterprise";

export const CompanyTypes: NonEmptyArray<CompanyType> = [
  "developer",
  "indie-hacker",
  "self-hosting",
  "startup",
  "enterprise"
];

export const CompanyTypeCheck = z.enum(CompanyTypes);

export type UserReferral =
  | "google"
  | "social-media"
  | "friend"
  | "github"
  | "blog"
  | "podcast"
  | "youtube"
  | "reddit"
  | "hackernews"
  | "other";

export const UserReferrals: NonEmptyArray<UserReferral> = [
  "google",
  "social-media",
  "friend",
  "github",
  "blog",
  "podcast",
  "youtube",
  "reddit",
  "hackernews",
  "other"
];

export const UserReferralCheck = z.enum(UserReferrals);

/** Additional attributes on the user as JSON. */
export interface UserAttributes {
  // Standard UTM parameters
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  // Comes from ?ref= it is either a website referral
  // or an internal referral code
  referrer?: string;
  // Google Ads params
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  // Facebook Ads params
  fbclid?: string;
  // IP address of the user
  ipAddress?: string;
  // Last-click attribution timestamp
  attributedAt?: Date;
  // Company type
  companyType?: CompanyType;
  // Heard from
  userReferral?: UserReferral;
  // ToS
  tosAcceptedAt?: Date;
  // Privacy policy
  privacyPolicyAcceptedAt?: Date;
  // Opt-in to marketing
  marketingOptIn?: boolean;
}

/**
 * Marketing attributes to be used for tracking user source when logging in or subscribing.
 * When one of them is updated, the `attributedAt` timestamp is set to the current date.
 */
export const UserAttributionKeys = [
  "utmSource",
  "utmMedium",
  "utmCampaign",
  "utmContent",
  "utmTerm",
  "referrer",
  "gclid",
  "fbclid",
  "gbraid",
  "wbraid"
] as const;

export type HostStatus = "active" | "error" | "draft" | "archived";
export type HostArch = "amd64" | "arm64";
export const HostArchTypes: NonEmptyArray<HostArch> = ["amd64", "arm64"];
export const HostArchCheck = z.enum(HostArchTypes);

export const HostStatuses: NonEmptyArray<HostStatus> = [
  "active",
  "error",
  "draft",
  "archived"
];
export const HostStatusCheck = z.enum(HostStatuses);
export type Host = PrismaHost;

export const HostNameSchema = z
  .string()
  .min(3)
  .max(32)
  .regex(/^[a-zA-Z0-9-]+$/)
  .describe("The name of the host, must be at least 3 characters and no special symbols");

export const HostCreateSchema = z
  .object({
    name: HostNameSchema.optional().describe(
      "The name of the host, must be at least 3 characters and no special symbols. If not provided, a random name will be generated."
    )
  })
  .optional();

export const HostUpdateSchema = z.object({
  name: z.string().min(3).optional(),
  privateKeyId: z.string().nullish(),
  privateKey: z.string().nullish(),
  description: z.string().nullish(),
  status: HostStatusCheck.optional(),
  ipAddress: z.string().nullish(),
  hostnames: z.array(z.string()).optional(),
  username: z.string().nullish(),
  password: z.string().nullish(),
  osType: z.string().nullish(),
  swap: z.number().nullish(),
  projectId: z.string().nullish(),
  env: z.record(z.string(), z.string().or(z.number()).or(z.boolean())).nullish()
});

export const HostAddHostnameSchema = z.object({
  hostname: z
    .string()
    .trim()
    .min(1)
    .max(253)
    .regex(
      /^(?=.{1,253}$)(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.(?!-)[A-Za-z0-9-]{1,63}(?<!-))*$/,
      "Invalid hostname format"
    )
});

export type ListHostsResponse = (Omit<Host, "health" | "privateKey" | "privateKeyId"> & {
  apps: Pick<App, "id" | "name" | "status">[];
})[];

export type AppType =
  | "next"
  | "react"
  | "react-static"
  | "docker-compose"
  | "native"
  | "java"
  | "php";
export type AppStatus = "active" | "error" | "archived" | "draft";
export const AppStatuses: NonEmptyArray<AppStatus> = ["active", "error", "archived", "draft"];
export const AppStatusCheck = z.enum(AppStatuses);
export type App = PrismaApp & { type: AppType; status: AppStatus | null };
export const AppTypes: { id: AppType; title: string }[] = [
  { id: "next", title: "Next.js" },
  { id: "react", title: "React" },
  { id: "react-static", title: "React Static" },
  { id: "docker-compose", title: "Docker Compose (also Python + Django)" },
  { id: "native", title: "Native" },
  { id: "java", title: "Java" },
  { id: "php", title: "PHP" }
];
export const AppTypeCheck = z.enum(AppTypes.map(t => t.id) as [AppType]);

export type TaskStatus = "queued" | "active" | "completed" | "cancelled" | "error";
export const TaskStatuses: NonEmptyArray<TaskStatus> = [
  "queued",
  "active",
  "completed",
  "cancelled",
  "error"
];
export const TaskStatusCheck = z.enum(TaskStatuses);
export type TaskType =
  | "build:app"
  | "test"
  | "prepare:host"
  | "deploy:app"
  | "backup:host"
  | "backup:app"
  | "launch"
  | "provider:host:create"
  | "provider:host:delete"
  | "remove:app"
  | "period:minute"
  | "period:hour"
  | "period:day"
  | "period:week"
  | "period:month"
  | "system:message";

export const TaskTypes: NonEmptyArray<TaskType> = [
  "build:app",
  "test",
  "prepare:host",
  "deploy:app",
  "backup:host",
  "backup:app",
  "launch",
  "provider:host:create",
  "provider:host:delete",
  "remove:app",
  "period:minute",
  "period:hour",
  "period:day",
  "period:week",
  "period:month"
];

export const TaskTypeCheck = z.enum(TaskTypes);

export type Task = PrismaTask & {
  type: TaskType;
  status?: TaskStatus | null;
  args?: TaskArgsType | null;
};

export type JournalType = "info" | "warn" | "error" | "health" | "log" | "ai";
export const JournalTypes: NonEmptyArray<JournalType> = [
  "info",
  "warn",
  "error",
  "health",
  "log",
  "ai"
];
export const JournalTypeCheck = z.enum(JournalTypes);
export type Journal = PrismaJournal;

export type Tenant = PrismaTenant;
export type TenantStatus = "active" | "disabled" | "blocked";
export const TenantStatuses: NonEmptyArray<TenantStatus> = ["active", "disabled", "blocked"];
export const TenantStatusCheck = z.enum(TenantStatuses);
export type PrivateKeyType = "ssh" | "apiKey";

export type PrivateKey = PrismaPrivateKey & {
  type?: PrivateKeyType | null;
};

export type TenantStripeAddons = {
  [key: string]: {
    quantity: number;
  };
};

export interface TenantMessagingTransport {
  id: string;
  title?: string;
  type: TransportType;
  enabled?: boolean;
  userId?: string;
  createdAt: Date;
  updatedAt: Date;
  lastSentAt?: Date;
  tested?: boolean;
  lastTestedAt?: Date;
  totalSent?: number;
  lastError?: string;
  lastErrorAt?: Date;
  [key: string]: unknown;
  notifications: SystemMessageType[];
}

export type TenantMessaging = {
  transports: TenantMessagingTransport[];
};

/** ID of a Provider, do, hetzner, etc */
export type ProviderType = "do" | "hetzner" | "aws" | "datacrunch" | "backup" | "github";
export const ProviderTypes: NonEmptyArray<ProviderType> = [
  "do",
  "hetzner",
  "aws",
  "datacrunch",
  "backup",
  "github"
];
export const ProviderTypeCheck = z.enum(ProviderTypes);
export type Provider = PrismaProvider;

/** For Github provider, we store information about github installation in auth. */
export type GithubProvider = Provider & { auth: GithubInstallation };

/** Installation information for a GitHub app, one per account. */
export interface GithubInstallation {
  id: number;
  account: {
    login: string;
    id: number;
    type: "User" | "Organization";
  };

  repository_selection: "all" | "selected";
}

/** Repository information for a GitHub installation. */
export interface GithubRepository {
  id: number;
  name: string;
  private: boolean;
  full_name: string;
  html_url: string;
}

/**
 * We have to define all as optional since the
 * same schema is used for partial updates as well.
 */
export const TaskArgsSchema = z.object({
  templateId: z.string().optional(),
  providerType: ProviderTypeCheck.optional(),
  instanceType: z.optional(z.string()),
  regionId: z.optional(z.string()),
  sshKeyId: z.optional(z.string()),
  hostId: z.optional(z.string()),
  hostname: z.optional(z.string()),
  username: z.optional(z.string()),
  // If template has allowAccessFrom set to ${USER_IPADDRESS}, this should be set to the IP address of the user
  ipAddress: z.optional(z.string()),
  appName: z.optional(z.string()),
  // For deleting the app task
  deleteApp: z.boolean().optional(),
  // For deleting the host task
  deleteHost: z.boolean().optional(),
  // Force fetching updated templates
  skipCache: z.coerce.boolean().optional(),
  message: MessageSchema.optional()
});

export type TaskArgsType = z.output<typeof TaskArgsSchema>;

export type ProvisionStatus = "active" | "error" | "completed" | "removed";
export const ProvisionStatuses: NonEmptyArray<ProvisionStatus> = [
  "active",
  "error",
  "completed",
  "removed"
];
export const ProvisionStatusCheck = z.enum(ProvisionStatuses);

export type Provision = PrismaProvision & {
  provider: ProviderType;
  status: ProvisionStatus | null;
};

export const PrivateKeyCreateSchema = z.object({
  privateKey: z.string().min(1, "SSH private key is required"),
  type: z.string(),
  name: z.string().min(3, "SSH key name must be at least 3 characters")
});

export type File = PrismaFile;

export type Backup = PrismaBackup;
export type BackupType = "app" | "service" | "volume";
export const BackupTypes: NonEmptyArray<BackupType> = ["app", "service", "volume"];

export type BackupStatus = "created" | "uploading" | "completed" | "error";
export const BackupStatuses: NonEmptyArray<BackupStatus> = [
  "created",
  "uploading",
  "completed",
  "error"
];
export const BackupStatusCheck = z.enum(BackupStatuses);

export type ServiceType =
  | "redis"
  | "postgres"
  | "docker"
  | "ddagent"
  | "mariadb"
  | "mongodb"
  | "firewall";

export const ServiceTypes: NonEmptyArray<ServiceType> = [
  "redis",
  "postgres",
  "docker",
  "ddagent",
  "mariadb",
  "mongodb",
  "firewall"
];

export const ServiceTypeCheck = z.enum(ServiceTypes);
export type ServiceStatus = "new" | "active" | "error";
export const ServiceStatuses: NonEmptyArray<ServiceStatus> = ["new", "active", "error"];
export const ServiceStatusCheck = z.enum(ServiceStatuses);
export type Service = PrismaService & {
  type: ServiceType | null;
  status?: ServiceStatus | null;
};

export const ServiceCreateSchema = z.object({
  type: ServiceTypeCheck
});

export const ServiceUpdateSchema = z.object({
  type: ServiceTypeCheck,
  env: z.record(z.string(), z.string())
});

export type JSONSafe<T> = {
  [K in keyof T]: T[K] extends Date
    ? string
    : T[K] extends Date | undefined
      ? string | undefined
      : T[K] extends Date | null
        ? string | null
        : T[K];
};

export interface HostHealth {
  hostname?: string;
  memory?: {
    total: number;
    used: number;
    free: number;
  };
  fs?: {
    total: number;
    used: number;
    free: number;
  };
  cpu?: {
    loadavg: number[];
    usage: number;
    count: number;
    arch: string;
    uptime?: number;
  };
  docker?: {
    containers: number;
    running: number;
    paused: number;
    stopped: number;
    memTotal: number;
    cpuTotal: number;
  };
}

interface AIUsageData {
  ai: {
    threadId: string;
    resourceId: string;
    runId?: string;
    provider?: string;
    modelId?: string;
    toolCalls?: number;
    inputTokens?: number;
    outputTokens?: number;
    reasoningTokens?: number;
    inputCost?: number;
    outputCost?: number;
    reasoningCost?: number;
    elapsed: number;
  };
}

export type JournalData = HostHealth & AIUsageData;

export const AIUsageDataSchema = z.object({
  ai: z.object({
    threadId: z.string(),
    resourceId: z.string(),
    runId: z.string().optional(),
    provider: z.string().optional(),
    modelId: z.string().optional(),
    toolCalls: z.number().optional(),
    inputTokens: z.number().optional(),
    outputTokens: z.number().optional(),
    elapsed: z.number()
  })
});

export const HostHealthSchema = z.object({
  hostname: z.string().optional(),
  memory: z
    .object({
      total: z.number(),
      used: z.number(),
      free: z.number()
    })
    .optional(),
  fs: z
    .object({
      total: z.number(),
      used: z.number(),
      free: z.number()
    })
    .optional(),
  cpu: z
    .object({
      loadavg: z.array(z.number()),
      usage: z.number(),
      count: z.number(),
      arch: z.string()
    })
    .optional()
});

export const JournalDataSchema = z.intersection(HostHealthSchema, AIUsageDataSchema);
export interface HostHealthJournal extends Omit<HostHealth, "hostname"> {
  now: number;
  timestamp: number;
  online: boolean;
}

export type ProjectProtection = "delete" | "deploy";
export const ProjectProtections: NonEmptyArray<ProjectProtection> = ["delete", "deploy"];
export const ProjectProtectionCheck = z.enum(ProjectProtections);

/** Types for Prisma, using overridable typing via prisma-json-types package */
declare global {
  /**
   * Prisma typings to be used in the Prisma schema.
   */

  namespace PrismaJson {
    type PrismaJournalHealth = JournalData;
    type PrismaJournalType = JournalType;
    type PrismaTaskType = TaskType;
    type PrismaTaskStatus = TaskStatus;
    type PrismaTaskArgs = TaskArgsType;
    type PrismaAppType = AppType;
    type PrismaAppStatus = AppStatus;
    type PrismaHostEnv = Record<string, string | number | boolean>;
    type PrismaAppEnv = Record<string, string | number | boolean>;
    type PrismaServiceType = ServiceType;
    type PrismaServiceEnv = Record<string, string | number | boolean>;
    type PrismaServiceStatus = ServiceStatus;
    type PrismaHostStatus = HostStatus;
    type PrismaPrivateKeyType = PrivateKeyType;
    type PrismaProviderType = ProviderType;
    type PrismaProvisionStatus = ProvisionStatus | null;
    type PrismaUserAttributes = UserAttributes;
    type PrismaBackupType = BackupType;
    type PrismaBackupStatus = BackupStatus;
    type PrismaTenantStatus = TenantStatus;
    type PrismaHostArch = HostArch;
    type PrismaHostHealth = HostHealth;
    type PrismaTenantStripeAddons = TenantStripeAddons;
    type PrismaTenantMessaging = TenantMessaging;
    type PrismaProjectProtect = ProjectProtection | null;
    type PrismaUserRole = UserRole;
    type PrismaUserSuggestionAction = UserSuggestionAction;
  }
}

type TOutputSchemaReturn<T> = T extends z.ZodType ? Promise<z.output<T>> : Promise<void>;

export interface KitContext {
  user: Pick<User, "id" | "tenantId">;
}

/** Defines kit method with input and output validation */
export type KitMethod<
  /** query parameters schema */
  TQuery extends z.ZodType | undefined,
  /** body if needed, e.g. POST, PATCH, PUT */
  TBody extends z.ZodType | undefined,
  /** Output schema */
  TOutputSchema extends z.ZodType | undefined
> = {
  title?: string;
  description?: string;
  query: TQuery;
  body: TBody;
  output: TOutputSchema;
} & (TQuery extends z.ZodType
  ? TBody extends z.ZodType
    ? {
        get: (
          query: z.output<TQuery>,
          body: z.output<TBody>,
          requestContext: KitContext
        ) => TOutputSchemaReturn<TOutputSchema>;
      }
    : {
        get: (
          query: z.output<TQuery>,
          requestContext: KitContext
        ) => TOutputSchemaReturn<TOutputSchema>;
      }
  : TBody extends z.ZodType
    ? {
        get: (
          query: undefined,
          body: z.output<TBody>,
          requestContext: KitContext
        ) => TOutputSchemaReturn<TOutputSchema>;
      }
    : {
        get: (requestContext: KitContext) => TOutputSchemaReturn<TOutputSchema>;
      });

export type Project = PrismaProject;

export type ProjectWithCounts = Project & { _count: { apps: number; hosts: number } };

export const ProjectSchema = z.object({
  id: z.string(),
  name: z.string().trim().min(1).max(32),
  description: z.string().nullable(),
  protect: ProjectProtectionCheck.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable(),
  userId: z.string(),
  tenantId: z.string()
});

export const ProjectCreateSchema = ProjectSchema.pick({
  name: true,
  description: true,
  protect: true
});

export const ProjectUpdateSchema = ProjectSchema.pick({
  name: true,
  description: true,
  protect: true
});

export const ProjectWithCountsSchema = ProjectSchema.extend({
  _count: z.object({ apps: z.number(), hosts: z.number() })
});

export type TypesAreEqual<T, U> = [T] extends [U] ? ([U] extends [T] ? true : false) : false;
export type StaticAssert<T extends true> = T;
type __check_ProjectSchema = StaticAssert<
  TypesAreEqual<Project, z.output<typeof ProjectSchema>>
>;

type __check_JournalDataSchema = StaticAssert<
  TypesAreEqual<JournalData, z.output<typeof JournalDataSchema>>
>;

export type UserRole = "admin";
export const UserRoles: UserRole[] = ["admin"];
export const UserRoleLabels: Record<UserRole, string> = {
  admin: "Admin"
};
export const UserRoleCheck = z.enum(UserRoles as [UserRole, ...UserRole[]]);

export type UserSuggestion = PrismaUserSuggestion;

export type UserSuggestionAction = "upvote" | "downvote" | "ignore";

export const UserSuggestionActions: NonEmptyArray<UserSuggestionAction> = [
  "upvote",
  "downvote",
  "ignore"
];
